import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('Missing Supabase env vars. Copy .env.example to .env.local and fill in values.');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Auth helpers ──────────────────────────────────────────────────────────────

export async function signUp(email, password, fullName, company) {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { full_name: fullName, company } }
  });
  if (error) throw error;
  return data;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// ── Profile helpers ───────────────────────────────────────────────────────────

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Dataset helpers ───────────────────────────────────────────────────────────

export async function uploadDataset(userId, file, rowCount, caseCount) {
  // Upload file to storage
  const path = `${userId}/${Date.now()}_${file.name}`;
  const { error: upErr } = await supabase.storage
    .from('datasets')
    .upload(path, file);
  if (upErr) throw upErr;

  // Save metadata to DB
  const { data, error } = await supabase
    .from('datasets')
    .insert({
      user_id: userId,
      name: file.name,
      file_path: path,
      row_count: rowCount,
      case_count: caseCount,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getDatasets(userId) {
  const { data, error } = await supabase
    .from('datasets')
    .select('*')
    .eq('user_id', userId)
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function deleteDataset(datasetId, filePath, userId) {
  await supabase.storage.from('datasets').remove([filePath]);
  const { error } = await supabase
    .from('datasets')
    .delete()
    .eq('id', datasetId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function downloadDataset(filePath) {
  const { data, error } = await supabase.storage
    .from('datasets')
    .download(filePath);
  if (error) throw error;
  return data; // Blob
}

// ── Report helpers ────────────────────────────────────────────────────────────

export async function saveReport(userId, datasetId, type, fileName, fileBlob) {
  // Upload report file
  const path = `${userId}/${Date.now()}_${fileName}`;
  const { error: upErr } = await supabase.storage
    .from('reports')
    .upload(path, fileBlob);
  if (upErr) throw upErr;

  // Save metadata
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  const { data, error } = await supabase
    .from('reports')
    .insert({
      user_id: userId,
      dataset_id: datasetId,
      type,
      file_name: fileName,
      file_path: path,
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getReports(userId) {
  const { data, error } = await supabase
    .from('reports')
    .select('*, datasets(name)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function downloadReport(filePath) {
  const { data, error } = await supabase.storage
    .from('reports')
    .download(filePath);
  if (error) throw error;
  return data;
}

export async function deleteReport(reportId, filePath, userId) {
  await supabase.storage.from('reports').remove([filePath]);
  const { error } = await supabase
    .from('reports')
    .delete()
    .eq('id', reportId)
    .eq('user_id', userId);
  if (error) throw error;
}

// ── Usage helpers ─────────────────────────────────────────────────────────────

export async function getUsage(userId) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const [{ count: uploads }, { count: reports }] = await Promise.all([
    supabase.from('datasets').select('*', { count: 'exact', head: true })
      .eq('user_id', userId).gte('uploaded_at', monthStart),
    supabase.from('reports').select('*', { count: 'exact', head: true })
      .eq('user_id', userId).gte('created_at', monthStart),
  ]);

  return { uploads: uploads || 0, reports: reports || 0 };
}

// ── Edge function call for report generation ──────────────────────────────────

export async function callGenerateReport(analyticsSummary, reportType) {
  const response = await fetch(
    'https://rscpzkjfmpfrmpjgmxtf.supabase.co/functions/v1/generate-report',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        'Authorization': 'Bearer ' + (process.env.REACT_APP_SUPABASE_ANON_KEY || ''),
      },
      body: JSON.stringify({ analyticsSummary, reportType })
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'HTTP ' + response.status);
  }
  return response.json();
}